import type * as React from 'react'
import { forwardRef, useState, useEffect } from 'react'
import { clsx } from 'clsx'

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'

import styles from './Chips.module.css'

type ChipsElement = React.ElementRef<typeof ToggleGroupPrimitive.ToggleGroup>
type ChipsItem = React.ElementRef<typeof ToggleGroupPrimitive.Item>

type ChipsBaseProps = Pick<
  React.AriaAttributes,
  'aria-label' | 'aria-labelledby'
> & {
  children: React.ReactNode
  /**
   * @default false
   */
  disabled?: boolean
  className?: string
  /**
   * When true, prevents deselecting the current selection.
   * - Single mode: Prevents clicking the selected chip to deselect it
   * - Multiple mode: Prevents deselecting the last remaining chips
   * At least one chip must remain selected.
   * @default false
   */
  required?: boolean
}

type ChipsSingleProps = ChipsBaseProps & {
  type?: 'single'
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
}

type ChipsMultipleProps = ChipsBaseProps & {
  type: 'multiple'
  value?: string[]
  defaultValue?: string[]
  onChange?: (value: string[]) => void
}

type ChipsProps = ChipsSingleProps | ChipsMultipleProps

const isSingleProps = (props: ChipsProps): props is ChipsSingleProps => {
  return !props.type || props.type === 'single'
}

const ChipsSingle = forwardRef<ChipsElement, ChipsSingleProps>((props, ref) => {
  const { children, disabled = false, className, required = false } = props

  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      required &&
      props.defaultValue !== undefined &&
      props.value === undefined
    ) {
      console.warn(
        'Chips: Using "required" with "defaultValue" is not recommended. Use "value" prop for controlled behavior to ensure proper required validation.',
      )
    }
  }, [required, props.defaultValue, props.value])

  const isControlled = props.value !== undefined
  const [internalValue, setInternalValue] = useState<string | undefined>(
    props.defaultValue,
  )

  // Sync internal state with controlled value
  useEffect(() => {
    if (isControlled) {
      setInternalValue(props.value)
    }
  }, [isControlled, props.value])

  const currentValue = isControlled ? props.value : internalValue

  const handleSingleChange = (newValue: string): void => {
    // If required and trying to deselect (empty value), prevent it
    if (required && !newValue) {
      return
    }

    if (!isControlled) {
      setInternalValue(newValue)
    }

    props.onChange?.(newValue)
  }

  return (
    <ToggleGroupPrimitive.Root
      ref={ref}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      value={currentValue}
      disabled={disabled}
      onValueChange={handleSingleChange}
      className={clsx(styles.wrapper, className)}
      orientation="horizontal"
      type="single"
    >
      {children}
    </ToggleGroupPrimitive.Root>
  )
})

ChipsSingle.displayName = 'ChipsSingle'

const ChipsMultiple = forwardRef<ChipsElement, ChipsMultipleProps>(
  (props, ref) => {
    const { children, disabled = false, className, required = false } = props

    useEffect(() => {
      if (
        process.env.NODE_ENV !== 'production' &&
        required &&
        props.defaultValue !== undefined &&
        props.value === undefined
      ) {
        console.warn(
          'Chips: Using "required" with "defaultValue" is not recommended. Use "value" prop for controlled behavior to ensure proper required validation.',
        )
      }
    }, [required, props.defaultValue, props.value])

    const isControlled = props.value !== undefined
    const [internalValues, setInternalValues] = useState<string[] | undefined>(
      props.defaultValue,
    )

    // Sync internal state with controlled value
    useEffect(() => {
      if (isControlled) {
        setInternalValues(props.value)
      }
    }, [isControlled, props.value])

    const currentValues = isControlled ? props.value : internalValues

    const handleMultipleChange = (newValue: string[]): void => {
      // If required and trying to deselect the last item, prevent it
      if (required && newValue.length === 0) {
        return
      }

      if (!isControlled) {
        setInternalValues(newValue)
      }

      props.onChange?.(newValue)
    }

    return (
      <ToggleGroupPrimitive.Root
        ref={ref}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        disabled={disabled}
        className={clsx(styles.wrapper, className)}
        orientation="horizontal"
        type="multiple"
        value={currentValues}
        onValueChange={handleMultipleChange}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    )
  },
)

ChipsMultiple.displayName = 'ChipsMultiple'

const Chips = forwardRef<ChipsElement, ChipsProps>((props, ref) => {
  if (isSingleProps(props)) {
    return <ChipsSingle ref={ref} {...props} />
  }

  return <ChipsMultiple ref={ref} {...props} />
})

Chips.displayName = 'Chips'

type ChipsItemProps = ToggleGroupPrimitive.ToggleGroupItemProps & {
  children: React.ReactNode
  value: string
  disabled?: boolean
  asChild?: boolean
  className?: string
}

const ChipsItem = forwardRef<ChipsItem, ChipsItemProps>(
  (
    {
      children,
      value,
      disabled = false,
      asChild = false,
      className,
      ...props
    }: ChipsItemProps,
    ref,
  ) => {
    return (
      <ToggleGroupPrimitive.Item
        ref={ref}
        value={value}
        disabled={disabled}
        className={clsx(styles.item, className)}
        asChild={asChild}
        {...props}
      >
        {children}
      </ToggleGroupPrimitive.Item>
    )
  },
)

ChipsItem.displayName = 'Chips.Item'

const ChipsObject = Object.assign(Chips, {
  Item: ChipsItem,
})

export { ChipsObject as Chips }
export type {
  ChipsProps,
  ChipsBaseProps,
  ChipsSingleProps,
  ChipsMultipleProps,
  ChipsItemProps,
}
